// Chip de filtro de selección múltiple para el listado de observaciones
// (handoff listado §2). Hermano de FilterDropdown — que sigue siendo de
// selección única y lo usan Observaciones (facility) y Tiendas — en vez
// de una extensión suya, para no mover su API.
//
// Disclosure, no listbox: el chip es un botón con aria-expanded +
// aria-controls (sin aria-haspopup) y el panel un grupo de casillas
// nativas con su conteo DENTRO del label. El panel queda abierto
// mientras se marcan opciones; se cierra con Esc (el foco vuelve al
// chip) o con un clic fuera (el foco no se mueve). Un solo panel
// abierto a la vez en toda la página.
import { useEffect, useId, useRef, useState } from "react";
import { Icon } from "./icons";

export type MultiOption<T extends string> = { value: T; label: string; count: number };

type Props<T extends string> = {
  label: string;
  options: readonly MultiOption<T>[];
  value: readonly T[];
  onChange: (next: T[]) => void;
};

// Cierra los demás paneles cuando uno se abre (teclado incluido: abrir
// con Enter un chip no dispara el clic-fuera del que estaba abierto).
const OPEN_EVENT = "filter-multi:open";

export function FilterMultiDropdown<T extends string>({
  label,
  options,
  value,
  onChange,
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => {
    function onOther(e: Event) {
      if ((e as CustomEvent<string>).detail !== panelId) setOpen(false);
    }
    document.addEventListener(OPEN_EVENT, onOther);
    return () => document.removeEventListener(OPEN_EVENT, onOther);
  }, [panelId]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  function toggleOpen() {
    setOpen((v) => {
      if (!v) document.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: panelId }));
      return !v;
    });
  }

  function toggle(v: T) {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  }

  /** «Quitar filtro»: vacía, cierra y deja el foco en el chip siguiente
   *  (o en «Limpiar filtros» si este era el último). */
  function clear() {
    onChange([]);
    setOpen(false);
    const self = ref.current;
    setTimeout(() => {
      let sib = self?.nextElementSibling ?? null;
      while (sib) {
        const target = sib.matches("button")
          ? (sib as HTMLElement)
          : sib.querySelector<HTMLElement>("button");
        if (target && !(target as HTMLButtonElement).disabled) {
          target.focus();
          return;
        }
        sib = sib.nextElementSibling;
      }
      triggerRef.current?.focus();
    }, 0);
  }

  const active = value.length > 0;
  const shown =
    value.length === 1
      ? (options.find((o) => o.value === value[0])?.label ?? value[0])
      : value.length > 1
        ? String(value.length)
        : null;

  return (
    <div className="filter-dropdown" ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        className={`filter-chip${active ? " filter-chip--active" : ""}`}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={
          value.length === 0
            ? label
            : value.length === 1
              ? `${label}: ${shown}`
              : `${label}, ${value.length} seleccionadas`
        }
        onClick={toggleOpen}
      >
        <span className="filter-chip__label">{label}</span>
        {shown !== null && (
          <>
            <span className="filter-chip__sep" aria-hidden="true">
              ·
            </span>
            <span>{shown}</span>
          </>
        )}
        <Icon.ChevronDown s={12} />
      </button>
      {open && (
        <div
          id={panelId}
          className="filter-dropdown-panel fopt-panel"
          role="group"
          aria-label={`Filtrar por ${label.toLowerCase()}`}
        >
          {options.map((o) => {
            const checked = value.includes(o.value);
            return (
              <label
                key={o.value}
                className={`filter-dropdown-option fopt${!checked && o.count === 0 ? " fopt--zero" : ""}`}
              >
                <input type="checkbox" checked={checked} onChange={() => toggle(o.value)} />
                <span className="fopt__l">{o.label}</span>
                <span className="fopt__n">({o.count})</span>
              </label>
            );
          })}
          {active && (
            <button type="button" className="filter-dropdown-option fopt--reset" onClick={clear}>
              Quitar filtro
            </button>
          )}
        </div>
      )}
    </div>
  );
}
